import { useEffect, useRef } from 'react';
import { useLocation, useBlocker } from 'react-router';

import { MESSAGES } from '@/lib/constants';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useSubmitting } from '@/hooks/use-submitting';

/**
 * Custom hook to reset form data when user leaves the page or reloads
 * This ensures form data is only preserved while staying on the same page
 * Handles both:
 * 1. beforeunload event (page refresh, close tab, etc.)
 * 2. React Router navigation (clicking links, back/forward buttons, etc.)
 *
 * This hook combines warning functionality (like useBeforeUnloadWarning)
 * with form reset logic. Used in register pages where form state should
 * be cleared when leaving the page.
 *
 * @param resetFunction - Function to call when resetting form data
 * @param shouldReset - Optional condition to determine if reset should happen
 * @param hasUnsavedChanges - Boolean indicating if there are unsaved changes (for warning)
 */
export const usePageExitReset = (
  resetFunction: () => void,
  shouldReset: boolean = true,
  hasUnsavedChanges: boolean = false
) => {
  // Minimal transition type for react-router blocker callback

  const location = useLocation();
  const resetFunctionRef = useRef(resetFunction);
  const shouldResetRef = useRef(shouldReset);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const isInitialMount = useRef(true);
  const currentPath = useRef(location.pathname);
  const resetViaBlockerRef = useRef(false);
  const { isSubmitting, setIsSubmitting } = useSubmitting();

  const { setConfirmDialog } = useConfirmDialog();

  // Update refs when values change
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Block React Router navigation ONLY when NOT from action button/form
  const blocker = useBlocker(() => {
    // Block only if: has unsaved changes,
    return hasUnsavedChanges;
  });

  // Show confirmation dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (isSubmitting) {
        setIsSubmitting(false);
        blocker.proceed();
      } else {
        setConfirmDialog({
          title: MESSAGES.CONFIRM_LEAVE_PAGE_UNSAVED_CHANGES,
          confirmDialog: () => {
            // User confirmed, proceed with navigation and reset
            resetViaBlockerRef.current = true;
            if (shouldResetRef.current) {
              resetFunctionRef.current();
            }
            blocker.proceed();
          },
          confirmButtonText: '破棄',
          cancelButtonText: 'キャンセル',
          closeDialog: () => {
            // User cancelled, reset blocker
            blocker.reset();
          },
        });
      }
    }
  }, [blocker]);

  // Reset form when component mounts (page load/reload)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Reset form on initial mount to ensure clean state
      if (shouldResetRef.current) {
        resetFunctionRef.current();
      }
      return;
    }
    // Reset form when pathname changes (user navigates away from register page)
    // Only reset if navigation was not handled by blocker (i.e., no unsaved changes or silent reset)
    if (location.pathname !== currentPath.current && shouldResetRef.current) {
      // If we reset via blocker, skip this reset
      if (resetViaBlockerRef.current) {
        resetViaBlockerRef.current = false;
      } else {
        // Normal navigation without blocking (no unsaved changes), reset silently
        resetFunctionRef.current();
      }
    }
    currentPath.current = location.pathname;
  }, [location.pathname]); // Only depend on location.pathname

  // Handle beforeunload with warning before reset
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldResetRef.current && hasUnsavedChangesRef.current) {
        // Show browser's default unsaved changes dialog
        e.preventDefault();
        return '';
      }

      // If no unsaved changes, reset forms silently
      if (shouldResetRef.current) {
        resetFunctionRef.current();
      }
    };

    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Only run on mount/unmount
};
